'use client';

import { useState, useMemo } from "react";
import Link from "next/link";
import { useTranslations } from "@/i18n/client";
import Image from "next/image";
import type { BlogEntry } from "@/content/blog";
import { useLocale } from "@/i18n/context";
import { motion, AnimatePresence } from "framer-motion";

interface Props {
  blogEntries: BlogEntry[];
}

const POSTS_PER_PAGE = 6;

export function BlogContent({ blogEntries }: Props) {
  const { t, isLoading } = useTranslations();
  const { locale } = useLocale();
  const b = t('blog');

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("Todos");
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);

  if (isLoading) return null;

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set(blogEntries.map(e => e.category));
    return ["Todos", ...Array.from(cats)];
  }, [blogEntries]);

  // Filter logic
  const filteredEntries = useMemo(() => {
    return blogEntries.filter(entry => {
      const matchesSearch = 
        entry.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.cuerpo.some(p => p.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesFilter = activeFilter === "Todos" || entry.category === activeFilter;
      
      return matchesSearch && matchesFilter;
    });
  }, [blogEntries, searchQuery, activeFilter]);

  const isSearchingOrFiltering = searchQuery !== "" || activeFilter !== "Todos";
  
  // If not searching/filtering, show featured separately
  const featured = !isSearchingOrFiltering ? blogEntries.find((e) => e.destacado) : null;
  const rest = !isSearchingOrFiltering 
    ? blogEntries.filter((e) => !e.destacado)
    : filteredEntries;

  const visibleEntries = rest.slice(0, visibleCount);
  const hasMore = visibleCount < rest.length;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + POSTS_PER_PAGE);
  };

  return (
    <>
      {/* HERO */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-[72px] pb-10">
        <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)]">
          {b.hero.overline}
        </div>
        <h1 className="taller-page-title mt-6">
          {b.hero.title}
          <em className="not-italic font-bold text-[color:var(--color-accent)]">
            {b.hero.titleAccent}
          </em>
        </h1>
      </section>

      {/* SEARCH & FILTERS */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-12">
        <div className="flex flex-col md:flex-row gap-6 items-center justify-between border-b border-[color:var(--color-border)] pb-8">
          {/* Categories */}
          <div className="flex gap-2 flex-wrap order-2 md:order-1">
            {categories.map((cat) => {
              const isActive = activeFilter === cat;
              const label = cat === "Todos" ? b.filters.todos : cat;
              return (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveFilter(cat);
                    setVisibleCount(POSTS_PER_PAGE);
                  }}
                  className={`px-4 py-1.5 rounded-full font-mono text-[10px] tracking-[0.1em] uppercase border transition-all duration-300 ${
                    isActive 
                      ? "bg-[color:var(--color-accent)] border-[color:var(--color-accent)] text-white" 
                      : "border-[color:var(--color-border)] text-[color:var(--color-mute)] hover:border-[color:var(--color-accent)]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative w-full md:w-[300px] order-1 md:order-2">
            <input
              type="text"
              placeholder={b.search.placeholder}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setVisibleCount(POSTS_PER_PAGE);
              }}
              className="w-full bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-full px-5 py-2.5 font-sans text-[14px] text-[color:var(--color-ink)] focus:outline-none focus:border-[color:var(--color-accent)] transition-colors placeholder:text-[color:var(--color-mute)]/50"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED POST (Only if not filtering) */}
      {!isSearchingOrFiltering && featured && (
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-16"
        >
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-6">
            {b.featured.overline}
          </div>
          <article>
            <Link
              href={`/blog/${featured.slug}`}
              className="group block no-underline"
              aria-label={`${b.featured.overline}: ${featured.title}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-start">
                {featured.image ? (
                  <div className="relative w-full h-[320px] md:h-[400px] rounded-2xl overflow-hidden border border-[color:var(--color-border)]">
                    <Image
                      src={featured.image}
                      alt={featured.title}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority
                      unoptimized
                    />
                  </div>
                ) : (
                  <div
                    className="taller-placeholder-light rounded-2xl"
                    style={{ height: 400 }}
                    aria-hidden="true"
                  />
                )}
                <div className="flex flex-col justify-center py-4">
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--color-accent)] mb-4">
                    {featured.category}
                  </div>
                  <h2 className="font-sans font-bold text-[36px] md:text-[48px] leading-[1.05] tracking-tight text-[color:var(--color-ink)] m-0 group-hover:text-[color:var(--color-accent)] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="font-sans text-[17px] leading-[1.6] text-[color:var(--color-mute)] mt-6 mb-8 line-clamp-4">
                    {featured.excerpt}
                  </p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-[color:var(--color-accent)] border-b border-[color:var(--color-accent)] pb-0.5 group-hover:opacity-70 transition-opacity">
                      {b.featured.readMore}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[color:var(--color-mute)]">
                      {formatDate(featured.date)}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </article>
        </motion.section>
      )}

      {/* REMAINING POSTS GRID */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-28">
        <div className="pt-10 mb-10">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)]">
            {isSearchingOrFiltering ? (
              <>{filteredEntries.length} {b.list.posts} encontrados</>
            ) : (
              <>{b.list.overline} · {rest.length} {b.list.posts}</>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {visibleEntries.map((entry, idx) => (
              <motion.article
                key={entry.slug}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                className="group flex flex-col h-full border border-[color:var(--color-border)] rounded-2xl overflow-hidden bg-[color:var(--color-surface)] hover:border-[color:var(--color-accent)] transition-colors"
              >
                <Link
                  href={`/blog/${entry.slug}`}
                  className="block no-underline flex flex-col h-full"
                  aria-label={`${b.list.readMore} ${entry.title}`}
                >
                  {entry.image && (
                    <div className="relative w-full h-[220px] overflow-hidden">
                      <Image
                        src={entry.image}
                        alt={entry.title}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        unoptimized
                      />
                    </div>
                  )}
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 rounded bg-[color:var(--color-accent)]/10 text-[color:var(--color-accent)]">
                        {entry.category}
                      </span>
                      <time
                        dateTime={entry.date}
                        className="font-mono text-[9px] tracking-[0.12em] text-[color:var(--color-mute)]"
                      >
                        {formatDate(entry.date)}
                      </time>
                    </div>
                    <h3 className="font-sans font-bold text-[22px] leading-tight tracking-tight text-[color:var(--color-ink)] m-0 mb-4 group-hover:text-[color:var(--color-accent)] transition-colors line-clamp-2">
                      {entry.title}
                    </h3>
                    <p className="font-sans text-[14px] leading-[1.6] text-[color:var(--color-mute)] m-0 line-clamp-3">
                      {entry.excerpt}
                    </p>
                    <div className="mt-auto pt-8">
                      <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[color:var(--color-accent)] border-b border-[color:var(--color-accent)] inline-block pb-0.5 group-hover:opacity-70 transition-opacity">
                        {b.list.readMore}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        {/* Load More */}
        {hasMore && (
          <div className="mt-16 flex justify-center">
            <motion.button
              whileHover={{ 
                scale: 1.05, 
                backgroundColor: "var(--color-accent-bright)",
                boxShadow: "0 0 30px var(--color-accent-glow)" 
              }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLoadMore}
              className="px-10 py-4 rounded-full bg-[color:var(--color-accent)] text-[color:var(--color-bg)] font-sans font-bold text-[12px] tracking-[0.1em] uppercase transition-all duration-300 shadow-xl shadow-[color:var(--color-accent)]/20 cursor-pointer"
            >
              {b.pagination.loadMore}
            </motion.button>
          </div>
        )}

        {!hasMore && rest.length > POSTS_PER_PAGE && (
          <div className="mt-16 text-center">
            <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[color:var(--color-mute)]/40">
              {b.pagination.noMore}
            </span>
          </div>
        )}

        {filteredEntries.length === 0 && (
          <div className="text-center py-20">
            <p className="font-mono text-[14px] text-[color:var(--color-mute)] uppercase tracking-widest">
              No se han encontrado artículos
            </p>
          </div>
        )}
      </section>
    </>
  );
}
